<script setup lang="ts">
import { computed, ref } from 'vue'
import { Icon } from '@iconify/vue'
import ConditionModal from './ConditionModal.vue'

// ---- Types ----
interface ConditionClause {
  key: string
  op: string
  value: unknown
}

type AudienceNode =
  | { type: 'GROUP_AND' | 'GROUP_OR'; child: AudienceNode[] }
  | { type: 'CONDITION'; child: ConditionClause[] }

interface ConditionTag {
  id: number
  label: string
  clauses: ConditionClause[]
}

interface ConditionGroup {
  id: number
  operator: 'AND' | 'OR'
  conditions: ConditionTag[]
}

defineProps<{ modelValue?: AudienceNode | null }>()
const emit = defineEmits<{
  'update:modelValue': [value: AudienceNode | null]
}>()

// ---- State ----
const outerOperator = ref<'AND' | 'OR'>('OR')
const groups = ref<ConditionGroup[]>([])
const outerConditions = ref<ConditionTag[]>([])

let nextId = 1

// Modal state — tracks which level is requesting a condition
// level: 'outer' | groupId (number)
const modalVisible = ref(false)
const modalTarget = ref<'outer' | number>('outer')

const operatorOptions: Array<'AND' | 'OR'> = ['OR', 'AND']

// ---- Actions ----
function addGroup() {
  groups.value.push({
    id: nextId++,
    operator: 'AND',
    conditions: [],
  })
}

function removeGroup(groupId: number) {
  groups.value = groups.value.filter((g) => g.id !== groupId)
}

function openModal(target: 'outer' | number) {
  modalTarget.value = target
  modalVisible.value = true
}

function handleConfirm(payload: { label: string; clauses: ConditionClause[] }) {
  console.log('[push-campaign][ConditionBuilder] modal payload', payload)
  const tag: ConditionTag = { id: nextId++, label: payload.label, clauses: payload.clauses }

  if (modalTarget.value === 'outer') {
    outerConditions.value.push(tag)
  } else {
    const group = groups.value.find((g) => g.id === modalTarget.value)
    if (group) group.conditions.push(tag)
  }

  modalVisible.value = false
}

function removeOuterCondition(id: number) {
  outerConditions.value = outerConditions.value.filter((c) => c.id !== id)
}

function removeGroupCondition(groupId: number, conditionId: number) {
  const group = groups.value.find((g) => g.id === groupId)
  if (group) {
    group.conditions = group.conditions.filter((c) => c.id !== conditionId)
  }
}

function buildPayload(): AudienceNode | null {
  const children: AudienceNode[] = []

  // groups -> GROUP_(AND|OR)
  for (const g of groups.value) {
    const clauses = g.conditions.flatMap((t) => t.clauses).filter((c) => c?.key && c?.op)
    if (!clauses.length) continue
    children.push({
      type: g.operator === 'AND' ? 'GROUP_AND' : 'GROUP_OR',
      child: [{ type: 'CONDITION', child: clauses }],
    })
  }

  // outer conditions -> CONDITION (one node with all clauses)
  const outerClauses = outerConditions.value
    .flatMap((t) => t.clauses)
    .filter((c) => c?.key && c?.op)
  if (outerClauses.length) {
    children.push({ type: 'CONDITION', child: outerClauses })
  }

  if (!children.length) return null

  return {
    type: outerOperator.value === 'AND' ? 'GROUP_AND' : 'GROUP_OR',
    child: children,
  }
}

const apiPayload = computed(() => buildPayload())

function emitChange() {
  console.log('[push-campaign][ConditionBuilder] emit payload', apiPayload.value)
  emit('update:modelValue', apiPayload.value)
}
</script>

<template>
  <div class="flex gap-4 items-start w-full">
    <!-- Outer operator dropdown (OR / AND) -->
    <div class="shrink-0">
      <select
        v-model="outerOperator"
        @change="emitChange"
        class="bg-[#f7f7f7] border-none rounded-[4px] px-3 py-[14px] text-[#777] text-[16px] cursor-pointer outline-none appearance-none pr-8 relative"
      >
        <option v-for="op in operatorOptions" :key="op" :value="op">{{ op }}</option>
      </select>
    </div>

    <!-- Outer dashed container -->
    <div
      class="flex-1 border border-dashed border-[#cdcdcd] rounded-[4px] p-4 flex flex-col gap-[10px]"
    >
      <!-- Toolbar: Add group + Add condition -->
      <div class="flex gap-[22px] items-center">
        <button
          class="flex gap-[7px] items-center rounded-[4px] cursor-pointer hover:opacity-80"
          @click="addGroup"
        >
          <Icon icon="streamline-cyber:filter-plus" width="16" color="#0035ba" />
          <span class="font-bold text-[#0035ba] text-[10px]">Thêm nhóm điều kiện</span>
        </button>

        <button
          class="flex gap-[7px] items-center rounded-[4px] cursor-pointer hover:opacity-80"
          @click="openModal('outer')"
        >
          <Icon icon="fluent:filter-add-20-regular" width="16" color="#0035ba" />
          <span class="font-bold text-[#0035ba] text-[10px]">Thêm điều kiện</span>
        </button>
      </div>

      <!-- Condition groups (nested) -->
      <div v-for="group in groups" :key="group.id" class="flex gap-4 items-start w-full">
        <!-- Inner operator dropdown -->
        <div class="shrink-0">
          <select
            v-model="group.operator"
            @change="emitChange"
            class="bg-[#f7f7f7] border-none rounded-[4px] px-3 py-[14px] text-[#777] text-[16px] cursor-pointer outline-none appearance-none pr-8"
          >
            <option v-for="op in operatorOptions" :key="op" :value="op">{{ op }}</option>
          </select>
        </div>

        <!-- Inner dashed container -->
        <div
          class="relative flex-1 border border-dashed border-[#cdcdcd] rounded-[4px] p-4 flex flex-col gap-[10px]"
        >
          <!-- Remove group button -->
          <button
            class="absolute top-[-6px] right-[-6px] cursor-pointer hover:opacity-80"
            @click="
              () => {
                removeGroup(group.id)
                emitChange()
              }
            "
          >
            <Icon icon="carbon:close-filled" width="16" color="#e53935" />
          </button>

          <!-- Add condition button (inner) -->
          <div class="flex gap-[22px] items-center">
            <button
              class="flex gap-[7px] items-center rounded-[4px] cursor-pointer hover:opacity-80"
              @click="openModal(group.id)"
            >
              <Icon icon="fluent:filter-add-20-regular" width="16" color="#0035ba" />
              <span class="font-bold text-[#0035ba] text-[10px]">Thêm điều kiện</span>
            </button>
          </div>

          <!-- Inner condition tags -->
          <div
            v-for="condition in group.conditions"
            :key="condition.id"
            class="relative inline-flex gap-[10px] items-center bg-[#daeeff] px-4 py-[7px] rounded-[4px] self-start max-w-full"
          >
            <span
              class="text-[#414141] text-[12px] whitespace-nowrap overflow-hidden text-ellipsis max-w-[300px]"
            >
              {{ condition.label }}
            </span>
            <button
              class="absolute top-[-6px] right-[-6px] cursor-pointer hover:opacity-80"
              @click="
                () => {
                  removeGroupCondition(group.id, condition.id)
                  emitChange()
                }
              "
            >
              <Icon icon="carbon:close-filled" width="16" color="#e53935" />
            </button>
          </div>
        </div>
      </div>

      <!-- Outer condition tags -->
      <div
        v-for="condition in outerConditions"
        :key="condition.id"
        class="relative inline-flex gap-[10px] items-center bg-[#daeeff] px-4 py-[7px] rounded-[4px] self-start max-w-full"
      >
        <span
          class="text-[#414141] text-[12px] whitespace-nowrap overflow-hidden text-ellipsis max-w-[500px]"
        >
          {{ condition.label }}
        </span>
        <button
          class="absolute top-[-6px] right-[-6px] cursor-pointer hover:opacity-80"
          @click="
            () => {
              removeOuterCondition(condition.id)
              emitChange()
            }
          "
        >
          <Icon icon="carbon:close-filled" width="16" color="#e53935" />
        </button>
      </div>
    </div>
  </div>

  <!-- Modal -->
  <ConditionModal
    :show="modalVisible"
    @on:close="modalVisible = false"
    @on:confirm="
      (payload) => {
        handleConfirm(payload)
        emitChange()
      }
    "
  />
</template>
